import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import CustomerDashboard from "@/pages/CustomerDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Eye, Plus, Package, Clock, ArrowRight, Bell, Truck, Heart, RotateCcw, User, MapPin, Save, Undo2, Gift, Ban, Quote, Star, Lock, CheckCircle2, Hourglass } from "lucide-react";

// Admin support view: renders the customer's OWN dashboard component in read-only mode.
// It used to be a hand-copied duplicate of the markup, which silently went stale the moment
// the real dashboard was redesigned. It now renders pages/CustomerDashboard.jsx directly with
// a preloaded payload from GET /admin/customers/{id}/dashboard (GET-only + audit-logged) and
// readOnly, so every mutating handler refuses. There is only one dashboard to keep in sync.

const EST_STATUS = {
  quoted: { label: "Upload Prescription", color: "bg-paw-warning/10 text-paw-warning border-paw-warning/20" },
  rx_uploaded: { label: "Pending Payment", color: "bg-blue-50 text-blue-600 border-blue-200" },
  rx_rejected: { label: "Action Needed", color: "bg-paw-error/10 text-paw-error border-paw-error/20" },
  awaiting_manual_quote: { label: "Preparing Quote", color: "bg-gray-100 text-gray-500 border-gray-200" },
};

const STATUS_CONFIG = {
  pending_payment: { label: "Pending Payment", color: "bg-paw-warning/10 text-paw-warning border-paw-warning/20" },
  pending_vet_review: { label: "Quality Check", color: "bg-blue-50 text-blue-600 border-blue-200" },
  under_vet_review: { label: "Quality Check", color: "bg-blue-50 text-blue-600 border-blue-200" },
  cosigned: { label: "Approved", color: "bg-paw-success/10 text-paw-success border-paw-success/20" },
  rejected: { label: "Rejected", color: "bg-paw-error/10 text-paw-error border-paw-error/20" },
  sent_to_fulfillment: { label: "At Fulfillment", color: "bg-purple-50 text-purple-600 border-purple-200" },
  being_prepared: { label: "Being Prepared", color: "bg-purple-50 text-purple-600 border-purple-200" },
  shipped: { label: "Shipped", color: "bg-paw-primary/10 text-paw-primary border-paw-primary/20" },
  delivered: { label: "Delivered", color: "bg-paw-success/10 text-paw-success border-paw-success/20" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500 border-gray-200" },
  refunded: { label: "Refunded", color: "bg-gray-100 text-gray-500 border-gray-200" },
};

const TABS = [
  { id: "orders", label: "Orders", icon: Package },
  { id: "profile", label: "Profile & Address", icon: User },
  { id: "testimonial", label: "Testimonial", icon: Quote },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export default function AdminCustomerView() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditReason, setCreditReason] = useState("");
  const [locks, setLocks] = useState([]);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockForm, setLockForm] = useState({ med_match: "", strength: "", pack_size: "", pack_price: "", reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/customers/${userId}/dashboard`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [userId]);

  // The ONLY writes this page can make. They live in the admin action strip ABOVE the
  // read-only frame, so the mirrored customer dashboard below stays completely inert.
  const grantCredit = async () => {
    setCreditBusy(true);
    try {
      await api.post(`/admin/customers/${userId}/free-delivery`, { reason: creditReason.trim(), months_valid: 12 });
      setCreditReason("");
      toast.success("Free delivery credit issued");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreditBusy(false);
    }
  };

  const revokeCredit = async () => {
    setCreditBusy(true);
    try {
      await api.delete(`/admin/customers/${userId}/free-delivery`);
      toast.success("Free delivery credit revoked");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreditBusy(false);
    }
  };

  // Price locks. A medication held at a promised price for THIS customer only. Loaded
  // separately from the dashboard payload because the dashboard endpoint is the customer's
  // own read-only mirror and must not start carrying admin-only pricing internals.
  const loadLocks = async () => {
    try {
      const res = await api.get(`/admin/users/${userId}/price-locks`);
      setLocks(res.data?.locks || []);
    } catch (e) {
      setLocks([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLocks(); }, [userId]);

  const addLock = async () => {
    const price = parseFloat(lockForm.pack_price);
    if (!lockForm.med_match.trim()) { toast.error("Enter the medication name to match"); return; }
    if (!(price > 0)) { toast.error("Enter a price per pack greater than zero"); return; }
    setLockBusy(true);
    try {
      await api.post(`/admin/users/${userId}/price-locks`, {
        med_match: lockForm.med_match.trim(),
        strength: lockForm.strength.trim() || null,
        pack_size: lockForm.pack_size ? parseInt(lockForm.pack_size, 10) : null,
        pack_price: price,
        reason: lockForm.reason.trim(),
        expires_at: null,
      });
      setLockForm({ med_match: "", strength: "", pack_size: "", pack_price: "", reason: "" });
      setLockOpen(false);
      toast.success("Price held for this customer");
      await loadLocks();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLockBusy(false);
    }
  };

  const removeLock = async (lockId) => {
    setLockBusy(true);
    try {
      await api.delete(`/admin/users/${userId}/price-locks/${lockId}`);
      toast.success("Held price removed");
      await loadLocks();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLockBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-paw-text-muted dark:text-white/30" /></div>;
  }
  if (!data) {
    return (
      <div className="px-3 sm:px-6 lg:px-10 py-10">
        <Link to="/admin/users" className="inline-flex items-center gap-2 font-manrope text-sm text-paw-text-secondary hover:text-paw-text dark:text-white/60 dark:hover:text-white"><ArrowLeft className="w-4 h-4" /> Back to Users &amp; Staff</Link>
        <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-6">Could not load this customer's dashboard.</p>
      </div>
    );
  }

  const { customer, orders, pending_estimates: pendingEstimates, rewards, notifications } = data;
  const activeOrders = (orders || []).filter((o) => !["delivered", "cancelled", "refunded"].includes(o.status));
  const pastOrders = (orders || []).filter((o) => ["delivered", "cancelled", "refunded"].includes(o.status));
  const unread = (notifications || []).filter((n) => !n.is_read).slice(0, 5);
  const estimates = pendingEstimates || [];
  const addressParts = customer.address_parts || null;

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-customer-view">
      <Link to="/admin/users" className="inline-flex items-center gap-2 font-manrope text-sm text-paw-text-secondary hover:text-paw-text dark:text-white/60 dark:hover:text-white mb-6" data-testid="customer-view-back">
        <ArrowLeft className="w-4 h-4" /> Back to Users &amp; Staff
      </Link>

      {/* Admin chrome: read-only banner + account facts the customer's own page doesn't show */}
      <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/40 dark:border-amber-500/25 rounded-xl px-4 py-3 mb-3" data-testid="customer-view-banner">
        <Eye className="w-4 h-4 text-amber-600 dark:text-amber-300 shrink-0" />
        <p className="font-manrope text-sm text-amber-800 dark:text-amber-200/90">
          Read-only view of <span className="font-semibold">{customer.first_name} {customer.last_name}</span>'s dashboard, exactly what they see. Nothing here can be clicked or changed, and this view is audit-logged.
        </p>
      </div>
      <div className="font-manrope text-xs text-paw-text-muted dark:text-white/45 mb-5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>{customer.email}</span>
        <span className="text-paw-text-muted dark:text-white/20">·</span>
        {customer.is_active ? <span className="text-emerald-700 dark:text-emerald-400">Active</span> : <span className="text-red-700 dark:text-red-400">Disabled</span>}
        {customer.is_guest && <><span className="text-paw-text-muted dark:text-white/20">·</span><span className="text-amber-700 dark:text-amber-300">Guest account</span></>}
        <span className="text-paw-text-muted dark:text-white/20">·</span>
        <span>Member since {fmtDate(customer.created_at) || "n/a"}</span>
      </div>

      {/* Admin actions. Deliberately OUTSIDE the read-only frame below. */}
      <div className="rounded-2xl border border-paw-border bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.03] p-4 mb-5" data-testid="admin-actions">
        <p className="font-outfit font-semibold text-sm text-paw-text dark:text-white/80 flex items-center gap-2">
          <Gift className="w-4 h-4 text-paw-gold" /> One-time free delivery
        </p>
        {(() => {
          const c = data.rewards?.free_shipping_credit;
          if (c && (c.status === "active" || c.status === "reserved")) {
            return (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="font-manrope text-sm text-emerald-700 dark:text-emerald-300">
                  Credit is on this account{c.status === "reserved" ? " and already applied to an open quote" : ""}
                  {c.reason ? ` · ${c.reason}` : ""}
                  {c.expires_at ? ` · expires ${fmtDate(c.expires_at)}` : ""}
                </span>
                <Button onClick={revokeCredit} disabled={creditBusy} className="rounded-xl bg-black/[0.06] hover:bg-red-500/15 text-paw-text hover:text-red-700 dark:bg-white/10 dark:hover:bg-red-500/20 dark:text-white/80 dark:hover:text-red-300 font-manrope h-9" data-testid="revoke-credit-btn">
                  {creditBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />} Revoke
                </Button>
              </div>
            );
          }
          if (c && c.status === "redeemed") {
            return (
              <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-2" data-testid="credit-redeemed">
                Already used{typeof c.amount_saved === "number" ? `, saving this customer $${c.amount_saved.toFixed(2)}` : ""}
                {c.redeemed_order_id ? ` on ${c.redeemed_order_id}` : ""}. Issue a new one below if needed.
              </p>
            );
          }
          return null;
        })()}
        {!(data.rewards?.free_shipping_credit && ["active", "reserved"].includes(data.rewards.free_shipping_credit.status)) && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="space-y-1.5 min-w-[240px] flex-1">
              <Label className="font-manrope text-xs text-paw-text-muted dark:text-white/50">Reason (kept in the audit log)</Label>
              <Input value={creditReason} onChange={(e) => setCreditReason(e.target.value)} placeholder="e.g. goodwill for the delayed inhaler" className="rounded-xl bg-white border-paw-border text-paw-text dark:bg-white/5 dark:border-white/10 dark:text-white h-9" data-testid="credit-reason" />
            </div>
            <Button onClick={grantCredit} disabled={creditBusy} className="rounded-xl bg-paw-gold hover:bg-paw-gold/90 text-paw-navy font-manrope font-semibold h-9" data-testid="grant-credit-btn">
              {creditBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />} Give free delivery
            </Button>
          </div>
        )}
        <p className="font-manrope text-xs text-paw-text-muted dark:text-white/35 mt-3">Zeroes the shipping on this customer's next paid order, once, automatically. It replaces the 50%-off refill rate rather than stacking with it, and cannot be spent twice.</p>
      </div>


      {/* Held prices. Also deliberately OUTSIDE the read-only frame below. */}
      <div className="rounded-2xl border border-paw-border bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.03] p-4 mb-5" data-testid="admin-price-locks">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-outfit font-semibold text-sm text-paw-text dark:text-white/80 flex items-center gap-2">
            <Lock className="w-4 h-4 text-paw-primary dark:text-blue-300" /> Held prices
          </p>
          <Button onClick={() => setLockOpen((v) => !v)} className="rounded-xl bg-black/[0.06] hover:bg-black/[0.1] text-paw-text dark:bg-white/10 dark:hover:bg-white/15 dark:text-white/80 font-manrope h-9" data-testid="toggle-price-lock-form">
            <Plus className="w-4 h-4 mr-2" /> {lockOpen ? "Cancel" : "Hold a price"}
          </Button>
        </div>

        {locks.length === 0 && !lockOpen && (
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-3" data-testid="no-price-locks">
            No held prices. This customer pays the normal catalogue price.
          </p>
        )}

        {locks.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="price-lock-list">
            {locks.map((lk) => (
              <div key={lk.lock_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-paw-border bg-white px-3 py-2.5 dark:border-white/10 dark:bg-white/5">
                <div className="min-w-0">
                  <p className="font-manrope text-sm text-paw-text dark:text-white/85">
                    <span className="font-semibold">{lk.med_match}</span>
                    {lk.strength ? ` \u00b7 ${lk.strength}` : ""}
                    {lk.pack_size ? ` \u00b7 pack of ${lk.pack_size}` : ""}
                    {" \u00b7 "}
                    <span className="font-semibold text-paw-primary dark:text-blue-300">${Number(lk.pack_price || 0).toFixed(2)} per pack</span>
                  </p>
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-0.5">
                    {lk.active === false ? "Switched off \u00b7 " : ""}
                    {lk.reason || "No reason recorded"}
                    {lk.expires_at ? ` \u00b7 expires ${fmtDate(lk.expires_at)}` : " \u00b7 no expiry"}
                    {lk.created_by ? ` \u00b7 set by ${lk.created_by}` : ""}
                  </p>
                </div>
                <Button onClick={() => removeLock(lk.lock_id)} disabled={lockBusy} className="rounded-xl bg-black/[0.06] hover:bg-red-500/15 text-paw-text hover:text-red-700 dark:bg-white/10 dark:hover:bg-red-500/20 dark:text-white/80 dark:hover:text-red-300 font-manrope h-9" data-testid="remove-price-lock-btn">
                  {lockBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />} Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        {lockOpen && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="price-lock-form">
            <div className="space-y-1.5 lg:col-span-2">
              <Label className="font-manrope text-xs text-paw-text-muted dark:text-white/50">Medication name</Label>
              <Input value={lockForm.med_match} onChange={(e) => setLockForm({ ...lockForm, med_match: e.target.value })} placeholder="e.g. Fluticasone Propionate Inhaler" className="rounded-xl bg-white border-paw-border text-paw-text dark:bg-white/5 dark:border-white/10 dark:text-white h-9" data-testid="lock-med" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-manrope text-xs text-paw-text-muted dark:text-white/50">Strength (optional)</Label>
              <Input value={lockForm.strength} onChange={(e) => setLockForm({ ...lockForm, strength: e.target.value })} placeholder="110 mcg" className="rounded-xl bg-white border-paw-border text-paw-text dark:bg-white/5 dark:border-white/10 dark:text-white h-9" data-testid="lock-strength" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-manrope text-xs text-paw-text-muted dark:text-white/50">Pack size (optional)</Label>
              <Input value={lockForm.pack_size} onChange={(e) => setLockForm({ ...lockForm, pack_size: e.target.value })} placeholder="120" className="rounded-xl bg-white border-paw-border text-paw-text dark:bg-white/5 dark:border-white/10 dark:text-white h-9" data-testid="lock-pack-size" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-manrope text-xs text-paw-text-muted dark:text-white/50">Price per pack (USD)</Label>
              <Input value={lockForm.pack_price} onChange={(e) => setLockForm({ ...lockForm, pack_price: e.target.value })} placeholder="33.99" className="rounded-xl bg-white border-paw-border text-paw-text dark:bg-white/5 dark:border-white/10 dark:text-white h-9" data-testid="lock-price" />
            </div>
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
              <Label className="font-manrope text-xs text-paw-text-muted dark:text-white/50">Reason (kept on the record)</Label>
              <Input value={lockForm.reason} onChange={(e) => setLockForm({ ...lockForm, reason: e.target.value })} placeholder="e.g. price promised before the August catalogue change" className="rounded-xl bg-white border-paw-border text-paw-text dark:bg-white/5 dark:border-white/10 dark:text-white h-9" data-testid="lock-reason" />
            </div>
            <Button onClick={addLock} disabled={lockBusy} className="rounded-xl bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope font-semibold h-9" data-testid="save-price-lock-btn">
              {lockBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />} Hold this price
            </Button>
          </div>
        )}

        <p className="font-manrope text-xs text-paw-text-muted dark:text-white/35 mt-3">Holds one medication at a set price for this customer only, on every future quote, until you remove it. It changes the medication price and leaves shipping alone, so a free delivery credit still works normally. Quantity still multiplies, so a held price of $33.99 per pack means three packs cost $101.97.</p>
      </div>

      {/* ============ the customer's dashboard, as they see it ============ */}
      <div className="rounded-2xl border border-paw-border dark:border-white/10 overflow-hidden shadow-xl dark:shadow-2xl" data-testid="customer-view-frame">
        <div className="flex items-center gap-2 bg-paw-surface-alt border-b border-paw-border dark:bg-[#141720] dark:border-white/10 px-4 py-2">
          <span className="w-2.5 h-2.5 rounded-full bg-black/[0.12] dark:bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-black/[0.12] dark:bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-black/[0.12] dark:bg-white/15" />
          <span className="font-manrope text-[11px] text-paw-text-muted dark:text-white/40 ml-2">pawlinx.com/dashboard (read-only)</span>
        </div>

        <div className="bg-[#F7F8FA]">
          <CustomerDashboard mirror={data} readOnly />
        </div>
      </div>

      <p className="font-manrope text-xs text-paw-text-muted dark:text-white/35 mt-4">
        Snapshot taken now from the same queries the customer's dashboard runs. Secure prescription links are deliberately not exposed here.
      </p>
    </div>
  );
}
